import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { hasActiveSubscription } from '../../utils/subscriptionUtils';
import { messagesService } from '../../services/supabaseService';


const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState('inactive');
  const [privacyLevel, setPrivacyLevel] = useState('high');
  const [userGender, setUserGender] = useState('male');
  const [messageChannel, setMessageChannel] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (userProfile) {
      setUserGender(userProfile?.gender || 'male');
      setPrivacyLevel(userProfile?.privacyLevel || 'high');
      // Check if user has active subscription
      const isActive = hasActiveSubscription(userProfile);
      setSubscriptionStatus(isActive ? 'active' : 'inactive');
      loadUnreadCount();
      setupRealtimeMessageSubscription();
    }

    return () => {
      if (messageChannel) {
        messagesService?.unsubscribeFromMessages(messageChannel);
      }
    };
  }, [userProfile]);

  const loadUnreadCount = async () => {
    if (!user) return;
    try {
      const count = await messagesService?.getUnreadCount(user?.id);
      setUnreadMessages(count);
    } catch (error) {
      console.error('Load unread count error:', error);
    }
  };

  const setupRealtimeMessageSubscription = async () => {
    if (!user) return;
    
    try {
      // Subscribe to new conversations for real-time message notifications
      const channel = messagesService?.subscribeToNewConversations(user?.id, async () => {
        // Reload unread count when new message arrives
        loadUnreadCount();
      });
      setMessageChannel(channel);
    } catch (error) {
      console.error('Setup message subscription error:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/registration-login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const isAdmin = userProfile?.isAdmin === true;

  const navigationItems = [
    {
      label: 'Discover',
      path: '/search-discovery',
      icon: 'Search',
      description: 'Browse profiles anonymously'
    },
    {
      label: 'Messages',
      path: '/messaging-system',
      icon: 'MessageCircle',
      description: 'Private conversations',
      badge: unreadMessages > 0 ? unreadMessages : null
    },
    {
      label: 'Dashboard',
      path: '/member-dashboard',
      icon: 'LayoutDashboard',
      description: 'Your activity hub'
    },
    {
      label: 'Account',
      path: '/user-profile-settings',
      icon: 'User',
      description: 'Settings & privacy'
    },
    {
      label: 'Safety & Trust Center',
      path: '/safety-trust-center',
      icon: 'Shield',
      description: 'Your safety and trust settings'
    },
    ...(isAdmin
      ? [
          {
            label: 'Admin Panel',
            path: '/admin-panel',
            icon: 'Settings',
            description: 'Moderation & verification'
          }
        ]
      : [])
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const getPrivacyStatusColor = () => {
    switch (privacyLevel) {
      case 'high':
        return 'text-success';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getPrivacyStatusIcon = () => {
    switch (privacyLevel) {
      case 'high':
        return 'ShieldCheck';
      case 'medium':
        return 'Shield';
      case 'low':
        return 'ShieldAlert';
      default:
        return 'Shield';
    }
  };

  return (
    <>
      <header
        className={`sticky top-0 z-[100] w-full bg-card transition-smooth ${
          scrolled ? 'shadow-warm-md' : 'shadow-warm'
        }`}
      >
        <div className="mx-auto max-w-[1200px] px-4 lg:px-6">
          <div className="flex h-16 items-center justify-between">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => handleNavigation('/')}
            >
              <img 
                src="/assets/images/Logo_NoBG-1769596999637.png" 
                alt="Bamchik Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>

            <nav className="hidden md:flex items-center gap-1">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`relative flex items-center gap-2 px-4 py-2 rounded-lg transition-smooth ${
                    isActivePath(item?.path)
                      ? 'bg-primary/10 text-primary' :'text-foreground hover:bg-muted hover:text-primary'
                  }`}
                >
                  <Icon
                    name={item?.icon}
                    size={20}
                    color={isActivePath(item?.path) ? 'var(--color-primary)' : 'currentColor'}
                  />
                  <span className="font-caption font-medium">{item?.label}</span>
                  {item?.badge && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-accent-foreground">
                      {item?.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted cursor-pointer transition-smooth hover:bg-muted/80 ${getPrivacyStatusColor()}`}
                onClick={() => handleNavigation('/user-profile-settings')}
              >
                <Icon name={getPrivacyStatusIcon()} size={16} />
                <span className="text-xs font-caption font-medium capitalize">
                  {privacyLevel} Privacy
                </span>
              </div>

              {userGender === 'male' && (
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-smooth ${
                    subscriptionStatus === 'active' ?'bg-success/10 text-success hover:bg-success/20' :'bg-warning/10 text-warning hover:bg-warning/20'
                  }`}
                  onClick={() => handleNavigation('/subscription-payment')}
                >
                  <Icon
                    name={subscriptionStatus === 'active' ? 'Crown' : 'AlertCircle'}
                    size={16}
                  />
                  <span className="text-xs font-caption font-medium">
                    {subscriptionStatus === 'active' ? 'Premium' : 'Renew'}
                  </span>
                </div>
              )}

              {user && (
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-smooth text-sm font-medium"
                >
                  <Icon name="LogOut" size={16} />
                  Sign Out
                </button>
              )}
            </div>

            <button
              className="md:hidden p-2 rounded-lg hover:bg-muted transition-smooth"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[200] md:hidden">
          <div
            className="absolute inset-0 bg-background"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute inset-x-0 top-16 bottom-0 bg-card animate-slide-down overflow-y-auto">
            <div className="p-4 space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg transition-smooth ${
                    isActivePath(item?.path)
                      ? 'bg-primary/10 text-primary' :'bg-muted hover:bg-muted/80'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      name={item?.icon}
                      size={24}
                      color={isActivePath(item?.path) ? 'var(--color-primary)' : 'currentColor'}
                    />
                    <div className="text-left">
                      <div className="font-caption font-medium">{item?.label}</div>
                      <div className="text-xs text-muted-foreground">{item?.description}</div>
                    </div>
                  </div>
                  {item?.badge && (
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
                      {item?.badge}
                    </span>
                  )}
                </button>
              ))}

              <div className="pt-4 mt-4 border-t border-border space-y-2">
                <div
                  className={`flex items-center justify-between p-4 rounded-lg bg-muted cursor-pointer transition-smooth hover:bg-muted/80 ${getPrivacyStatusColor()}`}
                  onClick={() => handleNavigation('/user-profile-settings')}
                >
                  <div className="flex items-center gap-3">
                    <Icon name={getPrivacyStatusIcon()} size={24} />
                    <div>
                      <div className="font-caption font-medium">Privacy Status</div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {privacyLevel} protection active
                      </div>
                    </div>
                  </div>
                  <Icon name="ChevronRight" size={20} />
                </div>

                {userGender === 'male' && (
                  <div
                    className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-smooth ${
                      subscriptionStatus === 'active' ?'bg-success/10 text-success hover:bg-success/20' :'bg-warning/10 text-warning hover:bg-warning/20'
                    }`}
                    onClick={() => handleNavigation('/subscription-payment')}
                  >
                    <div className="flex items-center gap-3">
                      <Icon
                        name={subscriptionStatus === 'active' ? 'Crown' : 'AlertCircle'}
                        size={24}
                      />
                      <div>
                        <div className="font-caption font-medium">
                          {subscriptionStatus === 'active' ? 'Premium Active' : 'Subscription'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {subscriptionStatus === 'active' ?'Renews Feb 28, 2026' :'Renew to continue'}
                        </div>
                      </div>
                    </div>
                    <Icon name="ChevronRight" size={20} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;